import React, { useState } from 'react'
import { X, Plus, Trash2, Save, AlertCircle, Database, Download } from 'lucide-react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import crudService from '../../services/crudService'
import dbMetadataService from '../../services/dbMetadataService'

interface CrudField {
  id: string
  name: string
  type: 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'text' | 'select' | 'decimal'
  required: boolean
  max_length?: number
  default_value?: string
  is_primary_key: boolean
  is_searchable: boolean
  show_in_list: boolean
  show_in_form?: boolean
  select_options?: string[]
  precision?: number
  scale?: number
}

interface CreateCrudData {
  name: string
  table_name: string
  description: string
  nombre_menu?: string
  fields: CrudField[]
  has_api: boolean
  has_frontend: boolean
  permissions: {
    read: boolean
    create: boolean
    update: boolean
    delete: boolean
  }
  [key: string]: any // Para permitir campos dinámicos de relaciones
}

interface CreateCrudModalProps {
  isOpen: boolean
  onClose: () => void
}

const CreateCrudModal: React.FC<CreateCrudModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient()
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState<CreateCrudData>({
    name: '',
    table_name: '',
    description: '',
    nombre_menu: '',
    fields: [
      {
        id: '1',
        name: 'id',
        type: 'number',
        required: true,
        is_primary_key: true,
        is_searchable: false,
        show_in_list: true,
        show_in_form: true
      }
    ],
    has_api: true,
    has_frontend: true,
    permissions: {
      read: true,
      create: true,
      update: true,
      delete: true
    }
  })

  const [isImporting, setIsImporting] = useState(false)

  const { data: dbTables = [], isLoading: isTablesLoading } = useQuery<string[]>({
    queryKey: ['db_tables'],
    queryFn: () => dbMetadataService.getTables(),
    staleTime: 1000 * 60 * 5,
  })

  const handleImportFieldsFromTable = async () => {
    if (!formData.table_name) {
      toast.error('Selecciona o escribe un nombre de tabla primero')
      return
    }
    try {
      setIsImporting(true)
      const imported = await dbMetadataService.getCrudFields(formData.table_name)
      if (!imported || imported.length === 0) {
        toast('La tabla no tiene columnas detectables', { icon: 'ℹ️' })
        return
      }
      const fields: CrudField[] = imported.map((f, idx) => {
        const base: CrudField = {
          id: f.id ?? `${formData.table_name}-${f.name}-${idx}`,
          name: f.name,
          type: f.type as CrudField['type'],
          required: !!f.required,
          max_length: f.max_length,
          default_value: f.default_value,
          is_primary_key: !!f.is_primary_key,
          is_searchable: !!f.is_searchable,
          show_in_list: !!f.show_in_list,
          show_in_form: (f as any).show_in_form !== false,
          select_options: f.select_options,
          precision: (f as any).precision,
          scale: (f as any).scale,
        }
        if (base.type === 'decimal') {
          base.precision = base.precision ?? 18
          base.scale = base.scale ?? 2
        }
        return base
      })
      setFormData(prev => ({ ...prev, fields }))
      toast.success(`Importados ${fields.length} campos desde "${formData.table_name}"`)
    } catch (e: any) {
      toast.error(e?.message || 'No se pudieron importar los campos')
    } finally {
      setIsImporting(false)
    }
  }

  const createCrudMutation = useMutation({
    mutationFn: async (data: CreateCrudData) => {
      // 1) Crear CRUD
      const createdCrud = await crudService.createCrud(data as any)

      // 2) Preparar relaciones a crear (evitar duplicados consultando backend)
      let createdCount = 0
      let skippedCount = 0
      let totalDetected = 0

      if (createdCrud?.id) {
        const relationFields = data.fields.filter(f => f.name.endsWith('_id') && f.name !== 'id')
        totalDetected = relationFields.length

        // Consultar relaciones ya existentes en backend para este CRUD
        const existing = await crudService.getRelationsByCrudId(createdCrud.id)
        const existingByField = new Set<string>((existing || []).map((r: any) => r.fieldName))

        for (const field of relationFields) {
          const relationKey = `relation_${field.name}`
          const suggestedTable = field.name.replace('_id', 's')
          const relationConfig = (data as any)[relationKey] ?? {
            enabled: true,
            relatedTable: suggestedTable,
            displayField: 'descripcion'
          }

          // Solo crear relación si está habilitada y no existe ya por fieldName
          if (relationConfig?.enabled && relationConfig?.relatedTable) {
            if (existingByField.has(field.name)) {
              skippedCount++
              continue
            }
            try {
              const relatedTable = relationConfig.relatedTable
              // Validar/ajustar displayField con columnas reales
              let finalDisplayField = relationConfig.displayField || 'descripcion'
              try {
                const cols = await dbMetadataService.getColumns(relatedTable)
                const colNames = new Set((cols || []).map(c => c.name))
                if (!colNames.has(finalDisplayField)) {
                  const candidates = [
                    finalDisplayField,
                    'nombre', 'name', 'descripcion', 'description', 'titulo', 'title', 'label', 'codigo', 'email'
                  ]
                  const found = candidates.find(c => colNames.has(c))
                  if (found) {
                    finalDisplayField = found
                  } else if (Array.isArray(cols) && cols.length > 0) {
                    const stringCols = cols.filter(c => {
                      const t = String(c.data_type || '').toLowerCase()
                      return ['varchar','text','char','nvarchar','longtext','mediumtext','tinytext'].includes(t)
                    })
                    if (stringCols.length > 0) finalDisplayField = stringCols[0].name
                    else if (colNames.has('id')) finalDisplayField = 'id'
                  }
                }
              } catch (e) {
                console.warn(`No se pudieron obtener columnas de ${relatedTable}`, e)
              }
              await crudService.createRelation({
                crudId: createdCrud.id,
                fieldName: field.name,
                relatedTable,
                displayField: finalDisplayField,
                isActive: true
              })
              createdCount++
            } catch (error) {
              // Evitar romper el flujo: registrar y continuar
              console.warn(`No se pudo crear relación para ${field.name}:`, error)
            }
          }
        }
      }

      return { createdCrud, createdCount, skippedCount, totalDetected }
    },
    onSuccess: (payload: any) => {
      queryClient.invalidateQueries(['cruds'])
      queryClient.invalidateQueries(['user-menus-sidebar'])

      const { createdCount, skippedCount, totalDetected } = payload || {}
      if (totalDetected > 0) {
        toast.success(`CRUD creado. Relaciones: ${createdCount} creada(s), ${skippedCount} existente(s).`)
      } else {
        toast.success('CRUD creado exitosamente')
      }

      resetForm()
      onClose()
    },
    onError: () => {
      toast.error('Error al crear CRUD')
    }
  })

  const resetForm = () => {
    setCurrentStep(1)
    setFormData({
      name: '',
      table_name: '',
      description: '',
      nombre_menu: '',
      fields: [
        {
          id: '1',
          name: 'id',
          type: 'number',
          required: true,
          is_primary_key: true,
          is_searchable: false,
          show_in_list: true
        }
      ],
      has_api: true,
      has_frontend: true,
      permissions: {
        read: true,
        create: true,
        update: true,
        delete: true
      }
    })
  }

  const handleInputChange = (field: keyof CreateCrudData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleTableNameGeneration = (name: string) => {
    const tableName = name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
    setFormData(prev => ({ 
      ...prev, 
      name,
      table_name: tableName 
    }))
  }

  const addField = () => {
    const newField: CrudField = {
      id: Date.now().toString(),
      name: '',
      type: 'string',
      required: false,
      is_primary_key: false,
      is_searchable: true,
      show_in_list: true,
      show_in_form: true
    }
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }))
  }

  const updateField = (fieldId: string, updates: Partial<CrudField>) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map(field => 
        field.id === fieldId ? { ...field, ...updates } : field
      )
    }))
  }

  const removeField = (fieldId: string) => {
    if (formData.fields.length === 1) {
      toast.error('Debe tener al menos un campo')
      return
    }
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter(field => field.id !== fieldId)
    }))
  }

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.name && formData.table_name && formData.description)
      case 2:
        return (
          formData.fields.length > 0 &&
          formData.fields.every(field => {
            if (!field.name || !field.type) return false
            if (field.type === 'decimal') {
              const p = field.precision
              const s = field.scale
              if (p == null || s == null) return false
              if (typeof p !== 'number' || typeof s !== 'number') return false
              if (!(p > 0) || !(s >= 0) || s > p) return false
            }
            return true
          })
        )
      case 3:
        return true
      default:
        return false
    }
  }

  const getStep2ValidationError = (): string | null => {
    if (formData.fields.length === 0) return 'Agrega al menos un campo.'
    for (const field of formData.fields) {
      if (!field.name) return 'Cada campo debe tener nombre.'
      if (!field.type) return `El campo ${field.name} debe tener un tipo.`
      if (field.type === 'decimal') {
        const p = field.precision
        const s = field.scale
        if (p == null || s == null) return `Completa precisión y escala para el campo ${field.name}.`
        if (typeof p !== 'number' || typeof s !== 'number' || Number.isNaN(p) || Number.isNaN(s)) {
          return `Precisión/Escala inválida en ${field.name}.`
        }
        if (!(p > 0)) return `La precisión debe ser > 0 en ${field.name}.`
        if (!(s >= 0)) return `La escala debe ser ≥ 0 en ${field.name}.`
        if (s > p) return `La escala no puede ser mayor que la precisión en ${field.name}.`
      }
    }
    return null
  }

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3))
    } else {
      const msg = currentStep === 2 ? (getStep2ValidationError() ?? 'Por favor completa todos los campos requeridos') : 'Por favor completa todos los campos requeridos'
      toast.error(msg)
    }
  }

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const handleSubmit = () => {
    if (validateStep(3)) {
      createCrudMutation.mutate(formData)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg w-full max-w-4xl h-[90vh] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Crear Nuevo CRUD</h2>
            <p className="text-sm text-gray-500">Paso {currentStep} de 3</p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gray-50 shrink-0">
          <div className="flex items-center">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  step <= currentStep 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-gray-300 text-gray-600'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 mx-4 rounded ${
                    step < currentStep ? 'bg-primary-600' : 'bg-gray-300'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>Información Básica</span>
            <span>Campos</span>
            <span>Configuración</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del CRUD *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleTableNameGeneration(e.target.value)}
                  className="input w-full"
                  placeholder="Ej: Productos, Clientes, Ordenes"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del menú (opcional)
                </label>
                <input
                  type="text"
                  value={formData.nombre_menu || ''}
                  onChange={(e) => handleInputChange('nombre_menu' as any, e.target.value)}
                  className="input w-full"
                  placeholder="Ej: Gestión de Productos"
                />
                <p className="text-xs text-gray-500 mt-1">Si se deja vacío, se usará el nombre del CRUD.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de Tabla *
                </label>
                <input
                  type="text"
                  value={formData.table_name}
                  onChange={(e) => handleInputChange('table_name', e.target.value)}
                  className="input w-full"
                  placeholder="Ej: products, clients, orders"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Se genera automáticamente pero puedes modificarlo
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Seleccionar tabla existente (opcional)
                </label>
                <div className="flex gap-2 items-center">
                  <Database className="h-4 w-4 text-gray-500" />
                  <select
                    value={formData.table_name || ''}
                    onChange={(e) => handleInputChange('table_name', e.target.value)}
                    className="input w-full"
                    disabled={isTablesLoading}
                  >
                    <option value="">{isTablesLoading ? 'Cargando tablas...' : '— Selecciona una tabla —'}</option>
                    {dbTables.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">Puedes elegir de la base de datos o escribir el nombre manualmente</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="input w-full"
                  placeholder="Describe qué gestiona este CRUD..."
                />
              </div>
            </div>
          )}

          {/* Step 2: Fields */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Campos de la Entidad</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleImportFieldsFromTable}
                    disabled={!formData.table_name || isImporting}
                    className="btn btn-secondary btn-sm flex items-center"
                    title="Importar campos desde la tabla seleccionada"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {isImporting ? 'Importando...' : 'Importar de Tabla'}
                  </button>
                  <button
                    onClick={addField}
                    className="btn btn-primary btn-sm flex items-center"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Campo
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {formData.fields.map((field) => (
                  <div key={field.id} className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Nombre del Campo *
                        </label>
                        <input
                          type="text"
                          value={field.name}
                          onChange={(e) => {
                            // Normalizar nombre: reemplazar espacios por guiones bajos y eliminar caracteres especiales
                            const normalizedName = e.target.value
                              .toLowerCase()
                              .replace(/\s+/g, '_')
                              .replace(/[^a-z0-9_]/g, '')
                            updateField(field.id, { name: normalizedName })
                          }}
                          className="input w-full text-sm"
                          placeholder="nombre_campo"
                          disabled={field.is_primary_key}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Tipo *
                        </label>
                        <select
                          value={field.type}
                          onChange={(e) => {
                            const nextType = e.target.value as CrudField['type']
                            const updates: Partial<CrudField> = { type: nextType }
                            if (nextType !== 'string') {
                              updates.max_length = undefined
                            }
                            if (nextType === 'decimal') {
                              if (field.precision == null) updates.precision = 18
                              if (field.scale == null) updates.scale = 2
                            } else {
                              updates.precision = undefined
                              updates.scale = undefined
                            }
                            updateField(field.id, updates)
                          }}
                          className="input w-full text-sm"
                          disabled={field.is_primary_key}
                        >
                          <option value="string">VARCHAR</option>
                          <option value="number">INT</option>
                          <option value="boolean">TINYINT(1)</option>
                          <option value="date">DATE</option>
                          <option value="datetime">DATETIME</option>
                          <option value="decimal">DECIMAL</option>
                          <option value="text">TEXT</option>
                          <option value="select">ENUM (Selección)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Longitud Máxima
                        </label>
                        <input
                          type="number"
                          value={field.max_length || ''}
                          onChange={(e) => updateField(field.id, { 
                            max_length: e.target.value ? parseInt(e.target.value) : undefined 
                          })}
                          className="input w-full text-sm"
                          placeholder="255"
                          disabled={field.type !== 'string'}
                        />
                      </div>
                    </div>

                    {field.type === 'decimal' && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Precisión</label>
                          <input
                            type="number"
                            value={field.precision ?? ''}
                            onChange={(e) => updateField(field.id, { precision: e.target.value ? parseInt(e.target.value) : undefined })}
                            className="input w-full text-sm"
                            placeholder="18"
                            disabled={field.is_primary_key}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Escala</label>
                          <input
                            type="number"
                            value={field.scale ?? ''}
                            onChange={(e) => updateField(field.id, { scale: e.target.value ? parseInt(e.target.value) : undefined })}
                            className="input w-full text-sm"
                            placeholder="2"
                            disabled={field.is_primary_key}
                          />
                        </div>
                      </div>
                    )}

                    {field.type === 'select' && (
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Opciones (separadas por comas)
                        </label>
                        <input
                          type="text"
                          placeholder="Opción 1, Opción 2, Opción 3"
                          onChange={(e) => updateField(field.id, { 
                            select_options: e.target.value.split(',').map(opt => opt.trim()).filter(Boolean)
                          })}
                          className="input w-full text-sm"
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateField(field.id, { required: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-xs text-gray-700">Requerido</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={field.is_searchable}
                          onChange={(e) => updateField(field.id, { is_searchable: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-xs text-gray-700">Buscable</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={field.show_in_list}
                          onChange={(e) => updateField(field.id, { show_in_list: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-xs text-gray-700">En Lista</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={field.show_in_form !== false}
                          onChange={(e) => {
                            const checked = e.target.checked
                            updateField(field.id, { show_in_form: checked, required: checked ? field.required : false })
                          }}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-xs text-gray-700">Form alta/edición</span>
                      </label>

                      {!field.is_primary_key && (
                        <button
                          onClick={() => removeField(field.id)}
                          className="text-red-600 hover:text-red-800 flex items-center text-xs"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Eliminar
                        </button>
                      )}
                    </div>

                    {field.is_primary_key && (
                      <div className="mt-2 flex items-center text-xs text-blue-600">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Este es el campo clave primaria
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Configuration */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {/* Configuración de relaciones */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-3">🔗 Configurar Relaciones</h4>
                <div className="space-y-4">
                  {formData.fields.filter(f => f.name.endsWith('_id') && f.name !== 'id').length > 0 ? (
                    formData.fields
                      .filter(f => f.name.endsWith('_id') && f.name !== 'id')
                      .map(field => {
                        const suggestedTable = field.name.replace('_id', 's')
                        const fieldRelationKey = `relation_${field.name}`
                        return (
                          <div key={field.name} className="bg-white p-4 rounded border">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Campo: <span className="text-blue-600">{field.name}</span>
                                </label>
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={formData[fieldRelationKey]?.enabled !== false}
                                    onChange={(e) => {
                                      setFormData(prev => ({
                                        ...prev,
                                        [fieldRelationKey]: {
                                          ...prev[fieldRelationKey],
                                          enabled: e.target.checked,
                                          relatedTable: e.target.checked ? (prev[fieldRelationKey]?.relatedTable || suggestedTable) : '',
                                          displayField: e.target.checked ? (prev[fieldRelationKey]?.displayField || 'descripcion') : ''
                                        }
                                      }))
                                    }}
                                    className="rounded border-gray-300 text-blue-600 mr-2"
                                  />
                                  <span className="text-sm text-gray-600">Configurar relación</span>
                                </div>
                              </div>
                              
                              {formData[fieldRelationKey]?.enabled !== false && (
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Tabla Relacionada
                                    </label>
                                    <select
                                      value={formData[fieldRelationKey]?.relatedTable || suggestedTable}
                                      onChange={(e) => {
                                        setFormData(prev => ({
                                          ...prev,
                                          [fieldRelationKey]: {
                                            ...prev[fieldRelationKey],
                                            enabled: true,
                                            relatedTable: e.target.value,
                                            displayField: prev[fieldRelationKey]?.displayField || 'descripcion'
                                          }
                                        }))
                                      }}
                                      className="input w-full text-sm"
                                    >
                                      <option value={suggestedTable}>{suggestedTable} (sugerida)</option>
                                      {dbTables.filter(t => t !== formData.table_name).map(table => (
                                        <option key={table} value={table}>{table}</option>
                                      ))}
                                    </select>
                                  </div>
                                  
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Campo a Mostrar
                                    </label>
                                    <input
                                      type="text"
                                      value={formData[fieldRelationKey]?.displayField || 'descripcion'}
                                      onChange={(e) => {
                                        setFormData(prev => ({
                                          ...prev,
                                          [fieldRelationKey]: {
                                            ...prev[fieldRelationKey],
                                            enabled: true,
                                            relatedTable: prev[fieldRelationKey]?.relatedTable || suggestedTable,
                                            displayField: e.target.value
                                          }
                                        }))
                                      }}
                                      className="input w-full text-sm"
                                      placeholder="descripcion, nombre, titulo..."
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {formData[fieldRelationKey]?.enabled !== false && (
                              <div className="mt-2 text-xs text-gray-600">
                                💡 Se mostrará como select con opciones de "{formData[fieldRelationKey]?.relatedTable || suggestedTable}"
                              </div>
                            )}
                          </div>
                        )
                      })
                  ) : (
                    <div className="text-sm text-blue-900/80">
                      No se detectaron campos que terminen en "_id" en tus campos actuales. Si agregas campos como "categoria_id", la configuración de relaciones aparecerá aquí.
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-md font-medium text-gray-700 mb-2">Generar Componentes</h4>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.has_api}
                          onChange={(e) => handleInputChange('has_api', e.target.checked)}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">Generar API REST</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.has_frontend}
                          onChange={(e) => handleInputChange('has_frontend', e.target.checked)}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">Generar Frontend</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-700 mb-2">Permisos</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.permissions.read}
                          onChange={(e) => handleInputChange('permissions', {
                            ...formData.permissions,
                            read: e.target.checked
                          })}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">Leer</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.permissions.create}
                          onChange={(e) => handleInputChange('permissions', {
                            ...formData.permissions,
                            create: e.target.checked
                          })}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">Crear</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.permissions.update}
                          onChange={(e) => handleInputChange('permissions', {
                            ...formData.permissions,
                            update: e.target.checked
                          })}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">Editar</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.permissions.delete}
                          onChange={(e) => handleInputChange('permissions', {
                            ...formData.permissions,
                            delete: e.target.checked
                          })}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">Eliminar</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Resumen</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><span className="font-medium">Nombre:</span> {formData.name}</p>
                  <p><span className="font-medium">Tabla:</span> {formData.table_name}</p>
                  <p><span className="font-medium">Campos:</span> {formData.fields.length}</p>
                  <p><span className="font-medium">API:</span> {formData.has_api ? 'Sí' : 'No'}</p>
                  <p><span className="font-medium">Frontend:</span> {formData.has_frontend ? 'Sí' : 'No'}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 shrink-0">
          <div>
            {currentStep > 1 && (
              <button
                onClick={prevStep}
                className="btn btn-secondary"
              >
                Anterior
              </button>
            )}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="btn btn-secondary"
              disabled={createCrudMutation.isLoading}
            >
              Cancelar
            </button>
            
            {currentStep < 3 ? (
              <button
                onClick={nextStep}
                className="btn btn-primary"
              >
                Siguiente
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={createCrudMutation.isLoading}
                className="btn btn-primary flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                {createCrudMutation.isLoading ? 'Creando...' : 'Crear CRUD'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateCrudModal
